import { Fragment } from "react";

/** Kennungen im Text: `S-0504`, `UE-201`, auch kurze Meldungs-Ids aus einer UUID (`S-3F2D`, `meldungsIdAnzeige`). */
const KENNUNG = /\b([A-ZÄÖÜ]{1,3}-(?:\d[\dA-Za-z]*|[\dA-F]{4}))\b/g;

/** Meldungs-Id, die das Gerät mit `crypto.randomUUID()` vergibt (teststeuerung.md §10, ADR-0020). */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const UUID_IM_TEXT = /\b(Schuss|Anschuss|Notruf|Wildsichtung|Vermisstmeldung) ([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\b/gi;

export type Meldungsart = "Schuss" | "Notruf" | "Wildsichtung" | "Vermisstmeldung";

/** Präfix der Meldungs-Id je Meldungsart (ubiquitous-language.md „Meldungs-Id“: `S-…`, `N-…`, `V-…`, `W-…`). */
const PRAEFIX: Record<Meldungsart, string> = { Schuss: "S", Notruf: "N", Wildsichtung: "W", Vermisstmeldung: "V" };

/**
 * Meldungs-Id in der Anzeige (oberflaeche.md §1.5, V-21): Eine vom Gerät vergebene UUID erscheint kurz als Präfix der
 * Meldungsart und ihre ersten vier Zeichen in Großbuchstaben (`S-3F2D`), wie die Meldungs-Ids der Feature Files
 * (`S-0504`). Jede andere Meldungs-Id erscheint unverändert. Nur die Anzeige ist kurz; Befehle, Routen und Titel der
 * Warteschlange tragen weiter die vollständige Meldungs-Id.
 */
export function meldungsIdAnzeige(id: unknown, art: Meldungsart = "Schuss"): string {
  const text = String(id ?? "");
  return UUID.test(text) ? `${PRAEFIX[art]}-${text.slice(0, 4).toUpperCase()}` : text;
}

function artVon(wort: string): Meldungsart {
  const w = wort.toLowerCase();
  if (w === "notruf") return "Notruf";
  if (w === "wildsichtung") return "Wildsichtung";
  if (w === "vermisstmeldung") return "Vermisstmeldung";
  return "Schuss";
}

/** Text mit kurzen Meldungs-Ids: „Schuss <UUID>“ → „Schuss S-3F2D“, „Notruf <UUID>“ → „Notruf N-…“ (V-21). */
export function textMitKurzenMeldungsIds(text: string): string {
  return text.replace(UUID_IM_TEXT, (_t, wort: string, id: string) => `${wort} ${meldungsIdAnzeige(id, artVon(wort))}`);
}

/**
 * Text, in dem Kennungen (`S-0504`, `UE-201`, `N-0401`) nie getrennt werden (design/README §14 0g-3 (7)): jede Kennung
 * steht in einem eigenen Element `.kennung` (`white-space: nowrap`), sonst trennt Chrome nach dem Bindestrich. Vom Gerät
 * vergebene Meldungs-Ids erscheinen dabei kurz (`meldungsIdAnzeige`).
 */
export function TextMitKennungen({ text }: { text: string }) {
  const teile = textMitKurzenMeldungsIds(text).split(KENNUNG);
  return <>{teile.map((t, i) => (i % 2 ? <span key={i} className="kennung">{t}</span> : <Fragment key={i}>{t}</Fragment>))}</>;
}
